/**
 * Country business calendars for SLA clocks. Weekends differ across the GCC: the UAE moved to Saturday/Sunday
 * in 2022, while KSA, Qatar, Kuwait, Bahrain and Oman keep Friday/Saturday. Public holidays (including the Eid
 * dates, which follow the Hijri calendar and moon sighting) are tenant-maintained data, not hardcoded here.
 * Ramadan working hours and optional prayer-time pauses are part of the calendar (see addBusinessMinutes).
 */
import { zonedWallTimeToIso } from './display.ts';
import {
  DEFAULT_PRAYER_PLACE,
  prayerTimes,
  type AsrRule,
  type PrayerMethod,
  type PrayerName,
} from './prayer.ts';
export { DEFAULT_TIMEZONE, WEEKEND_DAYS, type CountryCode } from './countries.ts';
import { DEFAULT_TIMEZONE, WEEKEND_DAYS, type CountryCode } from './countries.ts';

/** Day of week of an instant in a time zone (0 = Sunday). */
export function weekdayIn(d: Date, timeZone: string): number {
  const name = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone }).format(d);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}

export function isWeekend(d: Date, country: CountryCode, timeZone = DEFAULT_TIMEZONE[country]): boolean {
  return WEEKEND_DAYS[country].includes(weekdayIn(d, timeZone));
}

/** Hijri (Umm al-Qura) year/month/day parts, for scheduling around Ramadan and Eid. */
export function hijriParts(d: Date, timeZone = 'Asia/Riyadh'): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone,
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value.replace(/\D/g, ''));
  return { year: get('year'), month: get('month'), day: get('day') };
}

export const isRamadan = (d: Date, timeZone?: string) => hijriParts(d, timeZone).month === 9;

export interface BusinessCalendar {
  timeZone: string;
  /** 0 = Sunday ... 6 = Saturday */
  weekendDays: readonly number[];
  /** "HH:MM" local wall time. */
  start: string;
  end: string;
  /** Local dates "YYYY-MM-DD" that are holidays. */
  holidays?: readonly string[];
  /** Shorter working hours during Ramadan (Hijri month 9 by the Umm al-Qura calendar), e.g. 09:00-15:00. */
  ramadan?: { start: string; end: string } | null;
  /** Pause the clock for prayers that fall inside working hours. */
  prayer?: {
    latitude: number;
    longitude: number;
    method: PrayerMethod;
    asr: AsrRule;
    /** Length of each pause in minutes. */
    minutes: number;
    prayers: readonly PrayerName[];
  } | null;
}

const hm = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return h! * 60 + m!;
};

/** Local calendar date "YYYY-MM-DD" of an instant in a zone. */
function localDate(d: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

const nextDate = (date: string) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

const wall = (date: string, minutes: number, timeZone: string) =>
  new Date(
    zonedWallTimeToIso(
      `${date}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
      timeZone,
    )!,
  );

/**
 * The working intervals of one local date, as instants: business hours (Ramadan hours during Ramadan), minus
 * prayer pauses. Empty on weekends and holidays.
 */
export function workingIntervals(date: string, cal: BusinessCalendar): { start: Date; end: Date }[] {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if (cal.weekendDays.includes(weekday) || cal.holidays?.includes(date)) return [];
  const midday = wall(date, 12 * 60, cal.timeZone);
  const ramadan = isRamadan(midday, cal.timeZone);
  const hours = ramadan && cal.ramadan ? cal.ramadan : cal;
  const open = wall(date, hm(hours.start), cal.timeZone);
  const close = wall(date, hm(hours.end), cal.timeZone);
  if (close <= open) return [];
  let intervals = [{ start: open, end: close }];
  if (cal.prayer && cal.prayer.prayers.length && cal.prayer.minutes > 0) {
    const times = prayerTimes(date, cal.prayer, cal.prayer.method, cal.prayer.asr, { ramadan });
    const pauses = cal.prayer.prayers
      .map((p) => times[p])
      .sort((a, b) => a.getTime() - b.getTime())
      .map((s) => ({ start: s, end: new Date(s.getTime() + cal.prayer!.minutes * 60_000) }));
    for (const pause of pauses) {
      intervals = intervals.flatMap((iv) => {
        if (pause.end <= iv.start || pause.start >= iv.end) return [iv];
        const out: { start: Date; end: Date }[] = [];
        if (pause.start > iv.start) out.push({ start: iv.start, end: pause.start });
        if (pause.end < iv.end) out.push({ start: pause.end, end: iv.end });
        return out;
      });
    }
  }
  return intervals;
}

/**
 * Adds working minutes to an instant: time only counts inside the calendar's working intervals (business hours,
 * or Ramadan hours, on working days that are not holidays, minus prayer pauses), in the calendar's zone. Used for
 * SLA due times (e.g. a WhatsApp complaint at 17:30 on a UAE Friday with a 60-minute target is due at 09:30 on
 * Monday, because the UAE weekend is Saturday/Sunday).
 */
export function addBusinessMinutes(from: Date, minutes: number, cal: BusinessCalendar): Date {
  const t0 = new Date(Math.ceil(from.getTime() / 60_000) * 60_000);
  let remaining = minutes * 60_000;
  let date = localDate(t0, cal.timeZone);
  for (let day = 0; day < 800; day++, date = nextDate(date)) {
    for (const iv of workingIntervals(date, cal)) {
      if (iv.end <= t0) continue;
      const start = iv.start > t0 ? iv.start : t0;
      const available = iv.end.getTime() - start.getTime();
      if (remaining <= available) return new Date(start.getTime() + remaining);
      remaining -= available;
    }
  }
  throw new Error('business calendar has no working time');
}

export function calendarFor(
  country: CountryCode,
  start = '09:00',
  end = '18:00',
  timeZone = DEFAULT_TIMEZONE[country],
): BusinessCalendar {
  return { timeZone, weekendDays: WEEKEND_DAYS[country], start, end };
}

/** A tenant's service-level settings (as stored on the tenant and edited in Settings → Service levels). */
export interface ServiceLevels {
  country: string;
  timezone: string;
  firstResponseMinutes: number;
  businessHours: { start: string; end: string };
  autoAssign: boolean;
  ramadanHours: { start: string; end: string } | null;
  prayerPauses: {
    enabled: boolean;
    minutes: number;
    prayers: PrayerName[];
    method: PrayerMethod | null;
    asr: AsrRule;
    latitude: number | null;
    longitude: number | null;
  };
}

const COUNTRIES: readonly CountryCode[] = ['AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'IN'];

/** The business calendar the SLA clock uses for a tenant (falls back to the country's defaults where unset). */
export function calendarFromServiceLevels(
  s: ServiceLevels,
  holidays: readonly string[] = [],
): BusinessCalendar {
  const country = (COUNTRIES as readonly string[]).includes(s.country) ? (s.country as CountryCode) : 'AE';
  const cal = calendarFor(country, s.businessHours.start, s.businessHours.end, s.timezone);
  const place = DEFAULT_PRAYER_PLACE[country]!;
  const p = s.prayerPauses;
  return {
    ...cal,
    holidays,
    ramadan: s.ramadanHours,
    prayer:
      p.enabled && p.prayers.length
        ? {
            latitude: p.latitude ?? place.latitude,
            longitude: p.longitude ?? place.longitude,
            method: p.method ?? place.method,
            asr: p.asr,
            minutes: p.minutes,
            prayers: p.prayers,
          }
        : null,
  };
}
